/**
 * Feature gate contracts — plan_features + tenant_plan_overrides.
 *
 * Used by:
 *   POST/GET/PATCH /subscription/plans/:planId/features
 *   POST/GET/PATCH /subscription/tenants/:tenantId/feature-overrides
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Known feature keys (non-exhaustive — new ones can be added without migration)
// ---------------------------------------------------------------------------

export const KNOWN_FEATURE_KEYS = [
  'recruitment.video_interviews',
  'training.scorm_player',
  'reports.scheduled_emails',
  'ai.gemini_features',
  'attendance.biometric_import',
  'subscription.feature_gating',
  'payroll.bulk_export',
  'performance.360_feedback',
  'helpdesk.sla_automation',
  'analytics.advanced_reports',
] as const;

// Free-form to allow new feature keys without contract edits, but KNOWN_FEATURE_KEYS
// provides the reference list for IDE autocomplete via constants.
export type FeatureKey = string;

// ---------------------------------------------------------------------------
// PlanFeature record (output)
// ---------------------------------------------------------------------------

export const PlanFeatureSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  featureKey: z.string().min(1),
  enabled: z.boolean(),
  quotaLimit: z.number().int().positive().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type PlanFeature = z.infer<typeof PlanFeatureSchema>;

// ---------------------------------------------------------------------------
// TenantPlanOverride record (output)
// ---------------------------------------------------------------------------

export const TenantPlanOverrideSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  featureKey: z.string().min(1),
  enabled: z.boolean(),
  quotaLimit: z.number().int().positive().nullable(),
  expiresAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type TenantPlanOverride = z.infer<typeof TenantPlanOverrideSchema>;

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export const UpsertPlanFeatureInputSchema = z.object({
  featureKey: z.string().min(1).max(200),
  enabled: z.boolean(),
  quotaLimit: z.number().int().positive().nullable().optional(),
});
export type UpsertPlanFeatureInput = z.infer<typeof UpsertPlanFeatureInputSchema>;

export const UpsertTenantOverrideInputSchema = z.object({
  featureKey: z.string().min(1).max(200),
  enabled: z.boolean(),
  quotaLimit: z.number().int().positive().nullable().optional(),
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export type UpsertTenantOverrideInput = z.infer<typeof UpsertTenantOverrideInputSchema>;

// ---------------------------------------------------------------------------
// List responses
// ---------------------------------------------------------------------------

export const ListPlanFeaturesResponseSchema = z.object({
  items: z.array(PlanFeatureSchema),
});
export type ListPlanFeaturesResponse = z.infer<typeof ListPlanFeaturesResponseSchema>;

export const ListTenantOverridesResponseSchema = z.object({
  items: z.array(TenantPlanOverrideSchema),
});
export type ListTenantOverridesResponse = z.infer<typeof ListTenantOverridesResponseSchema>;

// ---------------------------------------------------------------------------
// Upsert responses
// ---------------------------------------------------------------------------

export const UpsertPlanFeatureResponseSchema = z.object({
  feature: PlanFeatureSchema,
});
export type UpsertPlanFeatureResponse = z.infer<typeof UpsertPlanFeatureResponseSchema>;

export const UpsertTenantOverrideResponseSchema = z.object({
  override: TenantPlanOverrideSchema,
});
export type UpsertTenantOverrideResponse = z.infer<typeof UpsertTenantOverrideResponseSchema>;

// ---------------------------------------------------------------------------
// Delete response
// ---------------------------------------------------------------------------

export const DeleteFeatureResponseSchema = z.object({
  id: z.string().uuid(),
  deletedAt: z.string().datetime({ offset: true }),
});
export type DeleteFeatureResponse = z.infer<typeof DeleteFeatureResponseSchema>;
